import PropTypes from 'prop-types';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { colors, fontSize } from '../../theme';
import { Card } from './primitives';
import { fmtPrice } from './shared';

const Y_AXIS_PADDING_RATIO = 0.03;

function getNumericValues(series, keys) {
  return series
    .flatMap((point) => keys.map((key) => point?.[key]))
    .filter((value) => value !== null && value !== undefined && value !== '')
    .filter((value) => Number.isFinite(Number(value)))
    .map(Number);
}

function getChartDomain(series) {
  const values = getNumericValues(series, ['low', 'high', 'close', 'tenkan', 'kijun', 'span_a', 'span_b', 'chikou']);
  if (!values.length) {
    return ['auto', 'auto'];
  }

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const span = Math.max(maxValue - minValue, Math.abs(maxValue) * 0.02, 1);
  const padding = span * Y_AXIS_PADDING_RATIO;
  return [minValue - padding, maxValue + padding];
}

function isValueInsideDomain(value, domain) {
  if (!Number.isFinite(Number(value)) || !Array.isArray(domain) || domain.length !== 2) {
    return false;
  }
  return Number(value) >= domain[0] && Number(value) <= domain[1];
}

const chartPointShape = PropTypes.shape({
  date: PropTypes.string,
  close: PropTypes.number,
  tenkan: PropTypes.number,
  kijun: PropTypes.number,
  span_a: PropTypes.number,
  span_b: PropTypes.number,
  chikou: PropTypes.number,
  cloud_lower: PropTypes.number,
  bullish_cloud: PropTypes.number,
  bearish_cloud: PropTypes.number,
  is_projection: PropTypes.bool,
});

function ChartTooltip({ active = false, payload = [], label = '', t }) {
  if (!active || !payload?.length) return null;

  const points = payload.filter((item) => item.value !== null && item.value !== undefined);
  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.1, boxShadow: '0 6px 18px rgba(15, 23, 42, 0.08)' }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#0f172a', mb: 0.5 }}>{label}</Typography>
      {points.map((item) => (
        <Typography key={item.dataKey} sx={{ fontSize: '0.68rem', color: item.color || '#475569', mb: 0.2 }}>
          {item.name}: {fmtPrice(item.value)}
        </Typography>
      ))}
    </Box>
  );
}

ChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.shape({
    color: PropTypes.string,
    dataKey: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.number,
  })),
  label: PropTypes.string,
  t: PropTypes.func.isRequired,
};

function IchimokuCloudChart({ chart = null, signal = null, t }) {
  const series = chart?.series || [];
  if (!series.length) {
    return null;
  }

  const yDomain = getChartDomain(series);

  const actualSeries = series.filter((point) => !point.is_projection);
  const futureSeries = series.filter((point) => point.is_projection);
  const lastActualDate = actualSeries.length ? actualSeries[actualSeries.length - 1].date : null;
  const firstFutureDate = futureSeries.length ? futureSeries[0].date : null;
  const lastFutureDate = futureSeries.length ? futureSeries[futureSeries.length - 1].date : null;
  const tradeLevels = [
    { key: 'entry', value: signal?.entry, color: '#2563eb', label: t('ichimokuTransition.drawer.entry') },
    { key: 'stop', value: signal?.stop, color: '#ef4444', label: t('ichimokuTransition.drawer.stop') },
    { key: 'target_1', value: signal?.target_1, color: '#10b981', label: t('ichimokuTransition.drawer.target1Short') },
    { key: 'target_2', value: signal?.target_2, color: '#14b8a6', label: t('ichimokuTransition.drawer.target2Short') },
  ].filter((item) => Number.isFinite(Number(item.value)));
  const visibleTradeLevels = tradeLevels.filter((item) => isValueInsideDomain(item.value, yDomain));
  const hiddenTradeLevels = tradeLevels.filter((item) => !isValueInsideDomain(item.value, yDomain));
  const guideItems = [
    {
      key: 'tenkan',
      label: t('ichimokuTransition.drawer.tenkan'),
      text: t('ichimokuTransition.drawer.tenkanGuide'),
      color: '#3b82f6',
    },
    {
      key: 'kijun',
      label: t('ichimokuTransition.drawer.kijun'),
      text: t('ichimokuTransition.drawer.kijunGuide'),
      color: '#f59e0b',
    },
    {
      key: 'chikou',
      label: t('ichimokuTransition.drawer.chikou'),
      text: t('ichimokuTransition.drawer.chikouGuide'),
      color: '#64748b',
    },
  ];

  const cloudSegments = series.slice(0, -1).map((point, index) => {
    const nextPoint = series[index + 1];
    if (
      point?.date == null
      || nextPoint?.date == null
      || point?.span_a == null
      || point?.span_b == null
    ) {
      return null;
    }

    const isBullish = point.span_a >= point.span_b;
    const isProjected = Boolean(point.is_projection || nextPoint.is_projection);
    let fill = 'rgba(239,68,68,0.14)';
    if (isBullish) {
      fill = isProjected ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.18)';
    } else if (isProjected) {
      fill = 'rgba(239,68,68,0.10)';
    }

    return {
      key: `${point.date}-${nextPoint.date}`,
      x1: point.date,
      x2: nextPoint.date,
      y1: Math.min(point.span_a, point.span_b),
      y2: Math.max(point.span_a, point.span_b),
      fill,
    };
  }).filter(Boolean);

  return (
    <Card>
      <Box sx={{ p: 2 }}>
        <Box sx={{ mb: 1.25 }}>
          <Typography sx={{ fontSize: fontSize.base, fontWeight: 800, color: colors.gray[800] }}>
            {t('ichimokuTransition.drawer.cloudChart')}
          </Typography>
          <Typography sx={{ fontSize: fontSize.sm, color: colors.gray[500] }}>
            {t('ichimokuTransition.drawer.cloudChartSubtitle')}
          </Typography>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
            {guideItems.map((item) => (
              <Tooltip key={item.key} title={item.text} arrow>
                <Chip
                  size="small"
                  label={item.label}
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    bgcolor: `${item.color}14`,
                    color: item.color,
                    border: `1px solid ${item.color}22`,
                  }}
                />
              </Tooltip>
            ))}
          </Stack>
        </Box>
        {hiddenTradeLevels.length ? (
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.1 }}>
            {hiddenTradeLevels.map((item) => {
              const direction = Number(item.value) > yDomain[1] ? '↑' : '↓';
              return (
                <Chip
                  key={item.key}
                  size="small"
                  label={`${item.label} ${fmtPrice(item.value)} ${direction}`}
                  sx={{
                    height: 22,
                    fontSize: '0.66rem',
                    fontWeight: 800,
                    bgcolor: '#fff',
                    color: item.color,
                    border: `1px dashed ${item.color}`,
                  }}
                />
              );
            })}
          </Stack>
        ) : null}
        <Box sx={{ width: '100%', height: 260 }}>
          <ResponsiveContainer>
            <ComposedChart data={series} margin={{ top: 8, right: 8, left: -14, bottom: 4 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
              <XAxis dataKey="date" minTickGap={28} tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} domain={yDomain} width={64} tickFormatter={(value) => fmtPrice(value)} />
              <RechartsTooltip content={<ChartTooltip t={t} />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {firstFutureDate && lastFutureDate ? (
                <ReferenceArea x1={firstFutureDate} x2={lastFutureDate} fill="rgba(148,163,184,0.08)" ifOverflow="extendDomain" />
              ) : null}
              {cloudSegments.map((segment) => (
                <ReferenceArea
                  key={segment.key}
                  x1={segment.x1}
                  x2={segment.x2}
                  y1={segment.y1}
                  y2={segment.y2}
                  fill={segment.fill}
                  fillOpacity={1}
                  ifOverflow="extendDomain"
                />
              ))}
              {lastActualDate ? (
                <ReferenceLine x={lastActualDate} stroke="#94a3b8" strokeDasharray="3 3" ifOverflow="extendDomain" label={{ value: t('ichimokuTransition.drawer.today'), position: 'insideTopRight', fill: '#64748b', fontSize: 10 }} />
              ) : null}
              {visibleTradeLevels.map((item) => (
                <ReferenceLine
                  key={item.key}
                  y={item.value}
                  stroke={item.color}
                  strokeDasharray="4 4"
                  strokeWidth={1.2}
                  ifOverflow="discard"
                  label={{ value: item.label, position: 'right', fill: item.color, fontSize: 10 }}
                />
              ))}
              <Line type="monotone" dataKey="close" name={t('ichimokuTransition.drawer.close')} stroke="#0f172a" strokeWidth={2} dot={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="tenkan" name={t('ichimokuTransition.drawer.tenkan')} stroke="#3b82f6" strokeWidth={1.8} dot={false} isAnimationActive={false} connectNulls />
              <Line type="monotone" dataKey="kijun" name={t('ichimokuTransition.drawer.kijun')} stroke="#f59e0b" strokeWidth={1.8} dot={false} isAnimationActive={false} connectNulls />
              <Line type="monotone" dataKey="span_a" name={t('ichimokuTransition.drawer.spanA')} stroke="#10b981" strokeWidth={1.2} dot={false} isAnimationActive={false} connectNulls />
              <Line type="monotone" dataKey="span_b" name={t('ichimokuTransition.drawer.spanB')} stroke="#ef4444" strokeWidth={1.2} dot={false} isAnimationActive={false} connectNulls />
              <Line type="monotone" dataKey="chikou" name={t('ichimokuTransition.drawer.chikou')} stroke="#64748b" strokeDasharray="4 3" strokeWidth={1.2} dot={false} isAnimationActive={false} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Card>
  );
}

IchimokuCloudChart.propTypes = {
  chart: PropTypes.shape({
    as_of_date: PropTypes.string,
    displacement: PropTypes.number,
    series: PropTypes.arrayOf(chartPointShape),
  }),
  signal: PropTypes.shape({
    entry: PropTypes.number,
    stop: PropTypes.number,
    target_1: PropTypes.number,
    target_2: PropTypes.number,
  }),
  t: PropTypes.func.isRequired,
};

export default IchimokuCloudChart;